import React, {Component, useState, useEffect, useCallback, useRef, useLayoutEffect} from 'react';
import { createPortal } from 'react-dom';

import { InpaintTelea } from "../../../assets/js/inpaint.js";

import General from "../../../utils/General";

import RetouchControls from "./RetouchControls"

const TOOL_MOVE = "move"
const TOOL_WHITEN = "whiten"
const TOOL_ERASE = "erase"
const TOOL_PATCH = "patch"
const TOOL_SMOOTHEN = "smoothen"

const ACTION_NONE = "none"
const ACTION_DRAWING = "drawing"

const LOCK_TO_VIEWPORT = true

var REFRESHED = false
var startDist = null

function getDistance(touches) {
    let dx = touches[0].pageX - touches[1].pageX;
    let dy = touches[0].pageY - touches[1].pageY;
    return Math.sqrt(dx * dx + dy * dy);
}

function getOrExtendPath2D(points, path=null){

  points.forEach((point, i) => {
    if(i == 0){
      if(!path){
        path = new Path2D();
      }
      path.moveTo(point.x, point.y)
    }else{
      path.lineTo(point.x, point.y);
    }
  });

  return path
}

const Retouch = ({ state, image, onUpdated }) => {

  const [panOffset, setPanOffset] = useState({ x: 0, y: 0})
  const [panBoundary, setPanBoundary] = useState({
    x: {
      min: 0,
      max: 0
    },
    y: {
      min: 0,
      max: 0
    }
  })

  const [minZoom, setMinZoom] = useState(0.1)
  const [zoom, setZoom] = useState(1)

  const [startPanPosition, setStartPanPosition] = useState({ x: 0, y: 0})

  const [imageElement, setImageElement] = useState(null)

  const [viewport, setViewport] = useState({
    center: { x: 0, y: 0 },
    left: 0,
    top: 0
  })

  const [action, setAction] = useState(ACTION_NONE)
  const [tool, setTool] = useState(null)
  const [drawingPaths, setDrawingPaths] = useState([])
  const [isErasing, setIsErasing] = useState(true)

  const [smoothenMaskPath, setSmoothenMaskPath] = useState(null)
  const [whitenMaskPath, setWhitenMaskPath] = useState(null)
  const [eraseMaskPath, setEraseMaskPath] = useState(null)

  const imageCanvasRef = useRef(null);
  const canvasContainerRef = useRef(null)
  const drawCanvasRef = useRef(null)

  const [scaledImageCanvasOffset, setScaledImageCanvasOffset] = useState({ x: 0, y: 0})

  useLayoutEffect(() => {

    setupCanvas()
    loadImage()
  }, [canvasContainerRef])

  useEffect(() => {
    if(!imageElement){
      return
    }

    const img = new Image();
    img.onload = () => {
      setDrawingPaths([])
      createMaskPaths([])
      setImageElement({
        ...imageElement,
        image: img
      })
    }
    img.src = image.original;
  }, [image])

  useEffect(() => {
    if(!imageElement){
      return
    }
    redraw()
  }, [imageElement?.image?.src])

  useEffect(() => {
    if(!imageElement){
      return
    }
    redraw()
  }, [panOffset, zoom])

  useEffect(() => {
    if(!imageElement){
      return
    }
    const canvasContainer = canvasContainerRef.current;

    const canvasContainerBoundary = canvasContainer.getBoundingClientRect();

    const difWidth = Math.abs(canvasContainerBoundary.width - (imageElement.scaledWidth * zoom))
    const difHeight = Math.abs(canvasContainerBoundary.height - (imageElement.scaledHeight * zoom))

    setPanBoundary({
      x: {
        min: - (difWidth / 2),
        max: (difWidth / 2)
      },
      y: {
        min: - (difHeight / 2),
        max: (difHeight / 2)
      }
    })

  }, [imageElement, zoom])

  const setupCanvas = () => {
    const imageCanvas = imageCanvasRef.current
    const canvasContainer = canvasContainerRef.current;
    const drawCanvas = drawCanvasRef.current

    imageCanvas.width = canvasContainer.clientWidth;
    imageCanvas.height = canvasContainer.clientHeight;

    let center = {
      x: canvasContainer.clientWidth / 2,
      y: canvasContainer.clientHeight / 2,
    }

    setViewport({
      center,
      left: 0,
      top: 0,
    })

    drawCanvas.width = imageCanvas.width
    drawCanvas.height = imageCanvas.height

    setCanvasContextAttributes(drawCanvas, {
      strokeStyle: "rgb(255, 197, 47)"
    })

    setCanvasContextAttributes(imageCanvas)
  }

  const setCanvasContextAttributes = (canvas, options) => {
    const context = canvas.getContext('2d')
    context.lineWidth = 10;
    context.lineCap = 'round';
    context.lineJoin = 'round'
    context.imageSmoothingEnabled = false;

    if(options?.strokeStyle){
      context.strokeStyle = options.strokeStyle;
    }
  }

  const loadImage = () => {
  const canvasContainer = canvasContainerRef.current;
  const imageObj = new Image();
  imageObj.src = image.original;
  imageObj.crossOrigin = 'Anonymous';
  imageObj.onload = () => {
    const canvasContainerBoundary = canvasContainer.getBoundingClientRect();
    const devicePixelRatio = window.devicePixelRatio || 1; 

    const scaleToFitWidth = canvasContainerBoundary.width / (imageObj.width * devicePixelRatio);
    const scaleToFitHeight = canvasContainerBoundary.height / (imageObj.height * devicePixelRatio);
    const minZoom = Math.min(scaleToFitWidth, scaleToFitHeight);
    
    const adjustedMinZoom = minZoom * devicePixelRatio;

    const scaledImageWidth = imageObj.width;
    const scaledImageHeight = imageObj.height;

    const left = (canvasContainerBoundary.width - scaledImageWidth) / 2;
    const top = (canvasContainerBoundary.height - scaledImageHeight) / 2;

    setViewport(prevViewport => ({
      ...prevViewport,
      left,
      top
    }));

    setImageElement({
      image: imageObj,
      scaledWidth: scaledImageWidth,
      scaledHeight: scaledImageHeight
    });
    setMinZoom(adjustedMinZoom)
    setZoom(adjustedMinZoom);
  };
};

  const getAdjustedCoordinates = event => {
    const drawCanvas = drawCanvasRef?.current
    var clientX = (event.clientX - panOffset.x * zoom + scaledImageCanvasOffset.x) / zoom
    var clientY = (event.clientY - panOffset.y * zoom + scaledImageCanvasOffset.y) / zoom

    const drawCanvasBoundary = drawCanvas.getBoundingClientRect();

    clientX -= (drawCanvasBoundary.left / zoom)
    clientY -= (drawCanvasBoundary.top / zoom)

    return { clientX, clientY };
  };

  const getCanvasTranslation = () => {
    const imageCanvas = imageCanvasRef.current

    const scaledImageCanvasSize = {
      width: imageCanvas.width * zoom,
      height: imageCanvas.height * zoom
    }

    const scaledImageCanvasOffset = {
      x: (scaledImageCanvasSize.width - imageCanvas.width) / 2,
      y: (scaledImageCanvasSize.height - imageCanvas.height) / 2,
    }

    setScaledImageCanvasOffset(scaledImageCanvasOffset)

    let panX = panOffset.x * zoom
    let panY = panOffset.y * zoom
    let translateX = panX - scaledImageCanvasOffset.x
    let translateY = panY - scaledImageCanvasOffset.y

    return {
      x: translateX,
      y: translateY
    }
  }

  const createMaskPaths = paths => {
    let smoothenMaskPath = null
    let smoothenPaths = paths.filter(path => path.type == "smoothen")
    smoothenPaths.forEach((drawingPath, i) => {
      if(i == 0){
        smoothenMaskPath = new Path2D();
      }
      smoothenMaskPath = getOrExtendPath2D(drawingPath.points, smoothenMaskPath)
    });

    let whitenMaskPath = null
    let whitenPaths = paths.filter(path => path.type == "whiten")
    whitenPaths.forEach((drawingPath, i) => {
      if(i == 0){
        whitenMaskPath = new Path2D();
      }
      whitenMaskPath = getOrExtendPath2D(drawingPath.points, whitenMaskPath)
    });

    let eraseMaskPath = null
    let erasePaths = paths.filter(path => path.type == "erase")
    erasePaths.forEach((drawingPath, i) => {
      if(i == 0){
        eraseMaskPath = new Path2D();
      }
      eraseMaskPath = getOrExtendPath2D(drawingPath.points, eraseMaskPath)
    });
    setSmoothenMaskPath(smoothenMaskPath)
    setWhitenMaskPath(whitenMaskPath)
    setEraseMaskPath(eraseMaskPath)
  }

  const handleTouchStart = event => {
    var touch = event.touches[0];
    var mouseEvent = new MouseEvent("mousedown", {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    handleMouseDown(mouseEvent);
  }

  const handleMouseDown = event => {
    const { clientX, clientY } = getAdjustedCoordinates(event);

    if(tool == null){
      setTool(TOOL_MOVE)
      setAction(ACTION_NONE);
      setStartPanPosition({ x: clientX, y: clientY });
    }
    else if(tool && tool !== TOOL_MOVE){
      setAction(ACTION_DRAWING)
      const drawCanvas = drawCanvasRef.current
      const drawContext = drawCanvas.getContext('2d')

      drawContext.beginPath();

      const drawingPath = new Path2D();
      drawingPath.moveTo(clientX, clientY);
      setDrawingPaths(prevDrawingPaths => {

        let paths = [
          ...prevDrawingPaths,
          {
            type: tool,
            path: drawingPath,
            points: [{
              x: clientX,
              y: clientY
            }],
          }
        ]

        createMaskPaths(paths)
        return paths
      })
    }
  };


  const handleTouchEnd = event => {
    var mouseEvent = new MouseEvent("mouseup", {});
    handleMouseUp(mouseEvent);
    startDist = null

  }

  const handleMouseUp = event => {
    if(tool == TOOL_MOVE){
      setTool(null)
      setAction(ACTION_NONE);
      return
    }

    setAction(ACTION_NONE);

    let data = General.clone(state)

    const { top, left } = viewport

    // take image on offscreen canvas so we don't apply zoom / lose parts of image
    const imageCanvas = imageCanvasRef.current
    const offScreenCanvas = document.createElement('canvas');
    offScreenCanvas.width = imageElement.scaledWidth;
    offScreenCanvas.height = imageElement.scaledHeight;

    setCanvasContextAttributes(offScreenCanvas)

    const translation = { x: -left, y: -top}
    drawImage(offScreenCanvas, imageElement, left, top, translation, false)

    if(tool == TOOL_ERASE){
      const offScreenMaskCanvas = document.createElement('canvas');
      offScreenMaskCanvas.width = imageElement.scaledWidth;
      offScreenMaskCanvas.height = imageElement.scaledHeight;

      setCanvasContextAttributes(offScreenMaskCanvas, {
        strokeStyle: "rgb(255, 197, 47)"
      })

      const drawingPath = drawingPaths[drawingPaths.length - 1]
      drawPath(offScreenMaskCanvas, drawingPath, translation, false)

      vanish(offScreenCanvas, offScreenMaskCanvas, imageElement, left, top, translation)
    }

    data.retouched_image = {
      original: offScreenCanvas.toDataURL()
    }
    onUpdated(data)
  };

  const handleZoom = (delta) => {
    setZoom(prevZoom => {
      var newZoom = prevZoom += delta
      if(LOCK_TO_VIEWPORT){
        if(newZoom > 5){
          newZoom = 5
        }else if(newZoom <= minZoom){
          newZoom = minZoom
        }
      }
      return newZoom
    })
  }

  const handleWheel = event => {
    handleZoom(event.deltaY * -0.01)
  }

  const handleTouchMove = event => {
    if (event.touches.length === 2) {
      const currentDist = getDistance(event.touches);
      if(!startDist){
        startDist = currentDist
        return
      }

      const delta = (currentDist - startDist) * 0.01;
      handleZoom(delta)
      startDist = currentDist
      return
    }
    // drag
    var touch = event.touches[0];
    var mouseEvent = new MouseEvent("mousemove", {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    handleMouseMove(mouseEvent);
  }

  const handleMouseMove = event => {
    if(tool == TOOL_MOVE){
      handlePan(event)
    }
    else if(action == ACTION_DRAWING){
      redraw(event)
    }
  }

  const handlePan = event => {
    const { clientX, clientY } = getAdjustedCoordinates(event);

    const deltaX = clientX - startPanPosition.x;
    const deltaY = clientY - startPanPosition.y;

    let offsetX = panOffset.x + deltaX
    let offsetY = panOffset.y + deltaY

    if(LOCK_TO_VIEWPORT){
      if(offsetX < panBoundary.x.min){
        offsetX = panBoundary.x.min
      } else if(offsetX > panBoundary.x.max){
        offsetX = panBoundary.x.max
      }

      if(offsetY < panBoundary.y.min){
        offsetY = panBoundary.y.min
      } else if(offsetY > panBoundary.y.max){
        offsetY = panBoundary.y.max
      }
    }

    setPanOffset({
      x: offsetX,
      y: offsetY,
    });
  }

  const drawPath = (canvas, drawingPath, canvasTranslation, enableZoom=true) => {
    const context = canvas.getContext('2d')

    context.save()
    context.translate(canvasTranslation.x, canvasTranslation.y)

    if(enableZoom){
      context.scale(zoom, zoom)
    }

    if(drawingPath){
      context.stroke(drawingPath.path)
    }

    context.restore()

    if(action != ACTION_DRAWING){
      context.clearRect(0, 0, canvas.width, canvas.height)
    }
  }

  const drawImage = (canvas, imageElement, left, top, canvasTranslation, enableZoom=true) => {
    if(!imageElement?.image){
      return
    }

    const context = canvas.getContext('2d')

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.save()

    if(canvasTranslation){
      context.translate(canvasTranslation.x, canvasTranslation.y)
    }

    if(enableZoom){
      context.scale(zoom, zoom)
    }

    if(smoothenMaskPath){
      context.beginPath()
      context.stroke(smoothenMaskPath);
      context.filter = "blur(10px)";
      context.globalCompositeOperation = 'source-in';
      context.drawImage(imageElement.image, left, top, imageElement.scaledWidth, imageElement.scaledHeight);
    }

    context.filter = 'none';
    context.globalCompositeOperation = 'destination-atop';
    context.drawImage(imageElement.image, left, top, imageElement.scaledWidth, imageElement.scaledHeight);

    if(whitenMaskPath){
      context.globalCompositeOperation = 'screen';
      context.filter = "none";
      context.strokeStyle = "#FFFFFF40";
      context.stroke(whitenMaskPath);
    }

    context.restore()
    context.filter = 'none';
    context.globalCompositeOperation = 'source-over';

  }

  const vanish = (imageCanvas, drawCanvas, imageElement, left, top, translation)  => {
    let drawingPath = null
    if(drawingPaths.length > 0){
      drawingPath = drawingPaths[drawingPaths.length - 1]
    }
    if(!drawingPath){
      return
    }
    const imageContext = imageCanvas.getContext('2d')

    const imageData = imageContext.getImageData(0, 0, imageCanvas.width, imageCanvas.height);

    const imageDataWidth = imageData.width
    const imageDataHeight = imageData.height;

    var maskU8 = new Uint8Array(imageDataWidth * imageDataHeight)
    const drawContext = drawCanvas.getContext('2d')
    drawContext.save()
    drawContext.translate(translation.x, translation.y)
    const maskData = drawContext.getImageData(0, 0, imageCanvas.width, imageCanvas.height);
    for(var i = 0; i < maskData.data.length / 4; i++){
			var inPath = maskData.data[4 * i] > 0;
			if(!inPath){
        continue
      }
			var rad = 6
			for(var dx = -rad; dx <= rad; dx++){
				for(var dy = -rad; dy <= rad; dy++){
					if(dx * dx + dy * dy <= rad * rad){
						maskU8[i + dx + dy * imageDataWidth] = 1;
					}
				}
			}
		}

    for(var channel = 0; channel < 3; channel++){
      var imgU8 = new Uint8Array(imageDataWidth * imageDataHeight)
      for(var n = 0; n < imageData.data.length; n+=4){
        imgU8[n / 4] = imageData.data[n + channel]
      }
      imgU8 = InpaintTelea(imageDataWidth, imageDataHeight, imgU8, maskU8)

      for(var i = 0; i < imgU8.length; i++){
        imageData.data[4 * i + channel] = imgU8[i]
      }
    }
    imageContext.putImageData(imageData, 0, 0);
    drawContext.clearRect(0, 0, drawCanvas.width, drawCanvas.height)
    drawContext.restore()
  }

  const redraw = (event) => {
    const imageCanvas = imageCanvasRef.current
    const drawCanvas = drawCanvasRef.current

    const canvasContainer = canvasContainerRef.current;

    const imageContext = imageCanvas.getContext('2d')
    const drawContext = drawCanvas.getContext('2d')

    let drawingPathsNo = drawingPaths.length
    let drawingPath = drawingPathsNo > 0 ? drawingPaths[drawingPathsNo - 1] : null

    if(action == ACTION_DRAWING && drawingPath && event){
      const { clientX, clientY } = getAdjustedCoordinates(event)
      if(tool == TOOL_SMOOTHEN){
        smoothenMaskPath.lineTo(clientX, clientY);
      }
      else if(tool == TOOL_WHITEN){
        whitenMaskPath.lineTo(clientX, clientY);
      }
      drawingPath.path.lineTo(clientX, clientY);
      drawingPath.points.push({ x: clientX, y: clientY})
    }

    const canvasTranslation = getCanvasTranslation()

    const { left, top } = viewport

    drawImage(imageCanvas, imageElement, left, top, canvasTranslation)

    drawPath(drawCanvas, drawingPath, canvasTranslation)
  }

  return(
    <div
      ref={canvasContainerRef}
      className="canvasContainer"
      style={{
        height: "100%",
        width: "100%",
        position: "relative"
      }}
    >
      <canvas
        id="imageCanvas"
        className="imageCanvas"
        ref={imageCanvasRef}
        disabled={true}
      />

      <canvas
        id="drawCanvas"
        className="drawCanvas"
        ref={drawCanvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 3,
          opacity: tool == TOOL_ERASE ? 0.3 : 0
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />

      <RetouchControls
        state={state}
        onSelected={(tool) => {
          setTool(tool)
          setAction(ACTION_NONE)
        }}
        onApply={(tool) => {

        }}
        onCancel={() => {
          setTool(null)
          setAction(ACTION_NONE)
        }}
      />
    </div>
  );
}

export default Retouch;
